/* @flow */
import cx from 'classnames';
import * as React from 'react';
import invariant from 'invariant';

import CopySubscriberButton from 'groups/components/CopySubscriberButton';
import ErrorReporter from 'app/components/ErrorReporter';
import MediaObject from 'components/MediaObject';
import GroupAvatar from 'groups/components/GroupAvatar';
import GroupByCodeFetcher from 'groups/containers/GroupByCodeFetcher';
import GroupLink from 'groups/components/GroupLink';
import GroupName from 'groups/components/GroupName';
import Hovercard from 'components/Hovercard';
import HovercardBody from 'components/HovercardBody';
import Link from 'components/Link';
import LoadingIndicator from 'components/LoadingIndicator';
import ModalTrigger from 'components/ModalTrigger';
import Relationship from 'entities/Relationship';
import RelationshipModal from 'user/components/RelationshipModal';
import ScrollableArea from 'components/ScrollableArea';
import StartChatButton from 'user/components/StartChatButton';
import Text from 'components/Text';
import './styles/UserHovercard.scss';

export type Props = {
  relationship: ?Relationship,
  showSubscriptions: boolean,
  neverShowStartChat: ?boolean,
  className?: string,
};

type State = {
  scrollAreaScrolled: boolean,
};

class UserHovercard extends React.Component<Props, State> {
  state: State = {
    scrollAreaScrolled: false,
  };

  static defaultProps = {
    showSubscriptions: true,
  };

  handleScrollAreaScroll = () => {
    const { scrollArea } = this.refs;
    this.setState({ scrollAreaScrolled: !scrollArea.isScrolledToTop() });
  };

  renderSubText() {
    const { relationship } = this.props;
    invariant(relationship, 'renderSubText requires a relationship');

    const subscriber = relationship.getRelatedUser();

    const note = subscriber.getNote();

    if (note) {
      return (
        <Text className="UserHovercard-note" color="graphite" truncate>
          {note}
        </Text>
      );
    }

    const organizations = relationship.getCommonOrganizationsWithUser();
    return organizations.length > 0 ? (
      <Text className="UserHovercard-note" color="graphite" truncate>
        {organizations.first().getName()}
      </Text>
    ) : null;
  }

  renderCopySubscriberButton() {
    return (
      <li>
        <CopySubscriberButton
          className="UserHovercard-groups-copyButton"
          relationship={this.props.relationship}
          size="small"
        />
      </li>
    );
  }

  renderOwnedGroups() {
    const { relationship } = this.props;
    let subscriptions = [];

    if (relationship && this.props.showSubscriptions) {
      const subscriber = relationship.getRelatedUser();
      subscriptions = subscriber.getSubscriptions();
    }

    if (subscriptions.length === 0) {
      return null;
    }

    return (
      <div
        className={cx('UserHovercard-groups', {
          'UserHovercard-groups-scrolled': this.state.scrollAreaScrolled,
        })}>
        <Text
          className="UserHovercard-groups-title"
          color="graphite"
          size="xxxsmall"
          weight="semibold"
          uppercase>
          Classes joined
        </Text>
        <ScrollableArea
          ref="scrollArea"
          className="UserHovercard-groups-scrollArea"
          onScroll={this.handleScrollAreaScroll}
          showShadows>
          <ul className="UserHovercard-groups-list">
            {subscriptions.map(subscription => {
              const groupCode = subscription.getGroupCode();

              return (
                <GroupByCodeFetcher groupCode={groupCode} key={groupCode}>
                  {({ group, loading, error }) => {
                    if (loading) {
                      return null;
                    }

                    if (error || !group) {
                      return <ErrorReporter error="UserHovercard - failed to fetch group" />;
                    }

                    return (
                      <li>
                        <GroupLink
                          className="UserHovercard-groups-list-item"
                          group={group}
                          color="chalkboard">
                          <MediaObject gutter="xxsmall" figure="left">
                            <GroupAvatar
                              className="UserHovercard-groups-list-item-avatar"
                              group={group}
                            />
                            <Text truncate>
                              <GroupName group={group} />
                            </Text>
                          </MediaObject>
                        </GroupLink>
                      </li>
                    );
                  }}
                </GroupByCodeFetcher>
              );
            })}
            {this.renderCopySubscriberButton()}
          </ul>
        </ScrollableArea>
      </div>
    );
  }

  renderStartChat() {
    if (this.props.neverShowStartChat) {
      return null;
    }

    const { relationship } = this.props;
    invariant(relationship, 'renderStartChat requires a relationship');

    if (!relationship.isEligibleForChat()) {
      return null;
    }

    return (
      <StartChatButton
        className="UserHovercard-startChatButton"
        subscriber={relationship.getRelatedUser()}
        full
      />
    );
  }

  renderBody() {
    const { relationship } = this.props;
    invariant(relationship, 'renderBody requires a relationship');
    const subscriber = relationship.getRelatedUser();

    let name = (
      <h6 className="UserHovercard-name">
        <Text truncate>{subscriber.getName()}</Text>
      </h6>
    );

    // Only link to RelationshipModal if they have a relationship
    if (relationship.isEligibleForChat() || relationship.getSubscriptionsToMe().length > 0) {
      name = (
        <ModalTrigger modal={<RelationshipModal userId={subscriber.id} />}>
          <Link color="chalkboard">{name}</Link>
        </ModalTrigger>
      );
    }

    return (
      <div>
        {name}
        {this.renderSubText()}
        {this.renderOwnedGroups()}
        {this.renderStartChat()}
      </div>
    );
  }

  render() {
    const body = this.props.relationship ? this.renderBody() : <LoadingIndicator />;

    return (
      <Hovercard {...this.props} className={cx('UserHovercard', this.props.className)}>
        <HovercardBody>{body}</HovercardBody>
      </Hovercard>
    );
  }
}

export default UserHovercard;
